import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOMeta from '@/components/feature/SEOMeta';
import { aboutInfo } from '@/mocks/sports';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formPayload = new FormData(form);
    const honeypotValue = (formPayload.get('phone_alt') as string || '').trim();

    if (honeypotValue) {
      setFormStatus('success');
      setFormData({ name: '', email: '', message: '' });
      return;
    }

    setFormStatus('idle');
    setFormError('');

    try {
      const response = await fetch('https://readdy.ai/api/form/d9q4qeme3oq4jqumtgc0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(
          Array.from(formPayload.entries())
            .filter(([key]) => key !== 'phone_alt')
            .map(([k, v]) => [k, v.toString()])
        ).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormData({ name: '', email: '', message: '' });
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText || '提交失败，请稍后重试';
        if (typeof serverMsg === 'string' && (serverMsg.includes('spam') || serverMsg.includes('form data is spam'))) {
          setFormStatus('idle');
          setFormError('提交失败，请稍后重试');
        } else {
          setFormStatus('error');
          setFormError(serverMsg);
        }
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查网络连接后重试');
    }
  };

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: `关于${aboutInfo.brandName}`,
    description: aboutInfo.description,
    url: 'https://www.bbty.com/about',
    mainEntity: {
      '@type': 'Organization',
      name: aboutInfo.brandName,
      foundingDate: aboutInfo.founded,
      description: aboutInfo.description,
      url: 'https://www.bbty.com',
      contactPoint: {
        '@type': 'ContactPoint',
        contactType: 'customer service',
        telephone: aboutInfo.contact.phone,
        email: aboutInfo.contact.email,
        availableLanguage: ['Chinese'],
      },
      address: {
        '@type': 'PostalAddress',
        streetAddress: aboutInfo.contact.address,
      },
    },
  };

  return (
    <>
      <ScrollToTop />
      <SEOMeta
        title={`关于${aboutInfo.brandName} - 品牌介绍 | ${aboutInfo.brandName}官网`}
        description={aboutInfo.description}
        keywords={`${aboutInfo.brandName},${aboutInfo.brandName}官网,${aboutInfo.brandName}平台,${aboutInfo.brandName}介绍,体育平台`}
        canonicalPath="/about"
        schema={aboutSchema}
      />
      <Navbar />
      <main className="pt-16">
        <div className="bg-background-100 border-b border-background-200">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]}
            />
            <div className="w-full px-4 md:px-6 pb-8">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                关于{aboutInfo.brandName}
              </h1>
              <p className="mt-2 text-sm text-foreground-500">{aboutInfo.description}</p>
            </div>
          </div>
        </div>

        <section className="w-full px-4 md:px-6 py-10 md:py-14">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-5">
              {aboutInfo.stats.map((stat) => (
                <div key={stat.label} className="text-center p-5 bg-background-50 border border-background-200 rounded-lg">
                  <div className="text-2xl md:text-3xl font-heading font-bold text-primary-500">
                    {stat.value}
                  </div>
                  <div className="mt-1 text-xs text-foreground-500">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              <div>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">
                  品牌愿景
                </h2>
                <p className="mt-4 text-sm text-foreground-600 leading-relaxed">
                  {aboutInfo.vision}
                </p>
                <h3 className="mt-6 text-lg font-heading font-semibold text-foreground-900">
                  品牌使命
                </h3>
                <p className="mt-2 text-sm text-foreground-600 leading-relaxed">
                  {aboutInfo.mission}
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {aboutInfo.values.map((value) => (
                  <div key={value.title} className="p-5 bg-background-50 border border-background-200 rounded-lg">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-500">
                      <i className={`${value.icon} text-lg`}></i>
                    </div>
                    <h4 className="mt-3 text-sm font-heading font-semibold text-foreground-900">
                      {value.title}
                    </h4>
                    <p className="mt-1.5 text-xs text-foreground-500 leading-relaxed">
                      {value.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-10 md:py-14">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 text-center mb-10">
              发展历程
            </h2>
            <div className="relative">
              <div className="absolute left-1/2 top-0 bottom-0 w-px bg-background-200 hidden lg:block"></div>
              <div className="space-y-8">
                {aboutInfo.milestones.map((milestone, index) => (
                  <div key={milestone.year} className={`relative flex flex-col lg:flex-row items-center gap-4 ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'}`}>
                    <div className={`flex-1 ${index % 2 === 0 ? 'lg:text-right lg:pr-10' : 'lg:text-left lg:pl-10'} text-center lg:text-left`}>
                      <span className="text-xs font-medium text-primary-500">{milestone.year}</span>
                      <h4 className="mt-1 text-sm font-heading font-semibold text-foreground-900">{milestone.title}</h4>
                      <p className="mt-0.5 text-xs text-foreground-500">{milestone.description}</p>
                    </div>
                    <div className="w-3 h-3 rounded-full bg-primary-500 border-2 border-background-50 z-10 hidden lg:block"></div>
                    <div className="flex-1 hidden lg:block"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section id="contact" className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
              <div>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">
                  联系我们
                </h2>
                <p className="mt-2 text-sm text-foreground-500">
                  如有任何问题或建议，欢迎通过以下方式联系我们
                </p>
                <div className="mt-6 space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-500">
                      <i className="ri-mail-line"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-400">电子邮箱</p>
                      <p className="text-sm font-medium text-foreground-800">{aboutInfo.contact.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-500">
                      <i className="ri-phone-line"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-400">客服热线</p>
                      <p className="text-sm font-medium text-foreground-800">{aboutInfo.contact.phone}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-500">
                      <i className="ri-map-pin-line"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-400">公司地址</p>
                      <p className="text-sm font-medium text-foreground-800">{aboutInfo.contact.address}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <div className="bg-background-50 border border-background-200 rounded-lg p-6">
                  <h3 className="text-base font-heading font-semibold text-foreground-900 mb-4">
                    发送消息
                  </h3>
                  <form data-readdy-form="" onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <label htmlFor="name" className="block text-xs font-medium text-foreground-600 mb-1.5">
                        您的姓名
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full px-3 py-2.5 text-sm border border-background-200 rounded-lg bg-background-50 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-xs font-medium text-foreground-600 mb-1.5">
                        电子邮箱
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="w-full px-3 py-2.5 text-sm border border-background-200 rounded-lg bg-background-50 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                    <div>
                      <label htmlFor="message" className="block text-xs font-medium text-foreground-600 mb-1.5">
                        留言内容
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        required
                        maxLength={500}
                        rows={4}
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        className="w-full px-3 py-2.5 text-sm border border-background-200 rounded-lg bg-background-50 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 transition-colors resize-none"
                        placeholder="请输入您的留言内容（最多500字）"
                      ></textarea>
                      <p className="text-xs text-foreground-400 mt-1 text-right">{formData.message.length}/500</p>
                    </div>
                    <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly className="anti-spam-field" />
                    <button
                      type="submit"
                      className="w-full px-6 py-2.5 bg-primary-500 text-background-50 text-sm font-medium rounded-lg hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      提交留言
                    </button>
                    {formStatus === 'success' && (
                      <p className="text-sm text-green-600 text-center">感谢您的留言，我们会尽快回复！</p>
                    )}
                    {formStatus === 'error' && (
                      <p className="text-sm text-red-500 text-center">{formError}</p>
                    )}
                  </form>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}